import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/apiClient'

/** P0.55 (29/08/2026) -- BLACK Foundation. Hooks para los 3 endpoints nuevos de inteligencia
 * personal (`/me/evolution`, `/me/strategy-performance`, `/me/intelligence`). Todos devuelven
 * 403 si el plan no incluye la feature -- las páginas deben comprobar `plan_features` ANTES
 * de llamarlos (mismo patrón ya usado por `_require_bankroll_access` en el resto de la app),
 * nunca depender solo del 403 para decidir qué mostrar. */

export interface CategoryEvolutionStats {
  category: string
  label: string
  picks: number
  decided: number
  won: number
  win_rate: number | null
  roi: number | null
  profit_loss: number | null
  total_staked: number
  average_odds: number | null
  sample_status: 'ok' | 'insuficiente'
}

export interface OddsRangeStats {
  range: string
  picks: number
  decided: number
  won: number
  win_rate: number | null
  roi: number | null
  profit_loss: number | null
  total_staked: number
  average_odds: number | null
  sample_status: 'ok' | 'insuficiente'
}

export interface PersonalDrawdown {
  max_drawdown_amount: number
  max_drawdown_pct: number | null
  peak_before_drawdown: number
  sample_size: number
}

export interface MyEvolutionResponse {
  configured: boolean
  period_days: number | null
  total_bets: number
  total_decided: number
  by_category: CategoryEvolutionStats[]
  by_odds_range: OddsRangeStats[]
  drawdown: PersonalDrawdown | null
  min_sample_size: number
}

export function useMyEvolution(days?: number) {
  return useQuery<MyEvolutionResponse>({
    queryKey: ['mini-app', 'me', 'evolution', days ?? 'total'],
    queryFn: async () => (await apiClient.get('/api/mini-app/me/evolution', { params: days ? { days } : {} })).data,
    staleTime: 30_000,
  })
}

export interface PersonalStrategyRow {
  strategy_key: string
  label: string
  picks: number
  win_rate: number | null
  roi: number | null
  average_odds: number | null
  sample_status: 'ok' | 'insuficiente'
}

export interface MyStrategyPerformanceResponse {
  configured: boolean
  strategies: PersonalStrategyRow[]
  min_sample_size: number
  source: 'personal'
}

export function useMyStrategyPerformance() {
  return useQuery<MyStrategyPerformanceResponse>({
    queryKey: ['mini-app', 'me', 'strategy-performance'],
    queryFn: async () => (await apiClient.get('/api/mini-app/me/strategy-performance')).data,
    staleTime: 30_000,
  })
}

export interface PersonalInsight {
  type: string
  title: string
  message: string
  evidence: Record<string, unknown>
  sample_size: number
  period: string
}

export interface MyIntelligenceResponse {
  configured: boolean
  insights: PersonalInsight[]
  total_bets: number
}

export function useMyIntelligence() {
  return useQuery<MyIntelligenceResponse>({
    queryKey: ['mini-app', 'me', 'intelligence'],
    queryFn: async () => (await apiClient.get('/api/mini-app/me/intelligence')).data,
    staleTime: 30_000,
  })
}
